import * as z from "zod";
import { isoDate } from "./common";

export const CARD_TYPES = ["sports", "pokemon", "mtg", "yugioh", "comic"] as const;
export type CardType = (typeof CARD_TYPES)[number];

/** The model answers `null` for a field that does not apply (`sport` on a Pokémon card); the model type keeps it absent. */
const reading = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? undefined);

/** What the model read off a card. Every field is optional: the reading is whatever it could see. Snake_case is the wire form. */
export const CardInfo = z.object({
  card_type: reading(z.enum(CARD_TYPES)),
  card_ebay_type: reading(z.enum(["sports", "non_sports"])),
  sport: reading(z.string()),
  player: reading(z.string()),
  team: reading(z.string()),
  brand: reading(z.string()),
  year: reading(z.number()),
  card_number: reading(z.string()),
  card_full_name: reading(z.string()),
  parallel_type: reading(z.string()),
  insert_name: reading(z.string()),
  features: reading(z.array(z.string())),
  rarity: reading(z.string()),
  set_name: reading(z.string()),
  language: reading(z.string()),
  title: reading(z.string()),
  issue_number: reading(z.string()),
  publisher: reading(z.string()),
  grade: reading(z.string()),
  manual_grade: reading(z.string()),
  variant_cover: reading(z.string()),
  artist: reading(z.string()),
  slabbed: reading(z.boolean()),
});
export type CardInfo = z.output<typeof CardInfo>;

/**
 * The pricing source's own payload, carried through untouched.
 *
 * The grade-keyed prices are what the eBay links and the price picker read, so
 * they are named rather than left to a bare record. `.loose()` keeps everything
 * else: this is a third party's response and it grows fields without telling us,
 * which is why the model must not drop what it does not recognise.
 *
 * `id` is deliberately `unknown` — the source answers a string for some products
 * and a number for others.
 */
export const ScpData = z
  .object({
    id: z.unknown().optional(),
    name: z.string().optional(),
    "loose-price": z.number().optional(),
    "graded-price": z.number().optional(),
    "new-price": z.number().optional(),
    "gamestop-price": z.number().optional(),
    "manual-only-price": z.number().optional(),
    "cib-price": z.number().optional(),
    "condition-9-price": z.number().optional(),
    "condition-10-price": z.number().optional(),
    "condition-13-price": z.number().optional(),
    "condition-14-price": z.number().optional(),
    "condition-15-price": z.number().optional(),
    "condition-16-price": z.number().optional(),
    "condition-17-price": z.number().optional(),
    "condition-18-price": z.number().optional(),
    "condition-19-price": z.number().optional(),
    "condition-20-price": z.number().optional(),
  })
  .loose();
export type ScpData = z.output<typeof ScpData>;

/**
 * Where to look this card up, by name.
 *
 * A plain string map because the keys are the pricing source's, not ours — a
 * grade label, a marketplace — and it grows without the backend changing shape.
 */
export const SearchLinks = z.record(z.string(), z.string());
export type SearchLinks = z.output<typeof SearchLinks>;

/** A card in the shop's catalogue, as scans and lists refer to it. */
export const CardSummary = z.object({
  id: z.string(),
  storeId: z.string(),
  player: z.string(),
  team: z.string(),
  brand: z.string(),
  year: z.number(),
  cardNumber: z.string(),
  parallelType: z.string(),
  insertName: z.string(),
  cardType: z.string(),
  gradingCompany: z.string(),
  grade: z.number(),
  slabbed: z.boolean(),
  cardFullName: z.string().nullable(),
  setName: z.string().nullable(),
  features: z.array(z.string()),
  imageUrl: z.string().nullable(),
  backImageUrl: z.string().nullable(),
  /** What the shop asks, in cents. Null means nobody has priced it. */
  currentPriceCents: z.number().nullable(),
  floorPriceCents: z.number().nullable(),
  lastPricedAt: isoDate.nullable(),
  searchLinks: SearchLinks.nullable(),
  scpData: ScpData.nullable(),
  trackStock: z.boolean(),
  available: z.number(),
  /** Null means a customer's scan created it and nobody has reviewed it yet. */
  confirmedAt: isoDate.nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type CardSummary = z.output<typeof CardSummary>;

export const CardListItem = z.object({
  card: CardSummary,
  offerId: z.string().nullable(),
  channel: z.string().nullable(),
  externalProductId: z.string().nullable(),
  providerPriceCents: z.number().nullable(),
});
export type CardListItem = z.output<typeof CardListItem>;

export const CARD_LIST_FILTERS = ["all", "in-stock", "out-of-stock", "untracked"] as const;

export interface CardListQuery {
  filter?: (typeof CARD_LIST_FILTERS)[number];
  search?: string;
  limit?: number;
  cursor?: string;
}

export interface CardPage {
  cards: CardListItem[];
  nextCursor: string | null;
}

/** ESTIMATED: the pipeline's figure, accepted unchanged. SET: a person typed it. REPRICED: a fresh market lookup. */
export const PRICE_POINT_KINDS = ["ESTIMATED", "SET", "REPRICED"] as const;

export const CardPricePoint = z.object({
  kind: z.enum(PRICE_POINT_KINDS),
  priceCents: z.number(),
  source: z.string(),
  recordedAt: isoDate,
});
export type CardPricePoint = z.output<typeof CardPricePoint>;

export const CardLot = z.object({
  id: z.string(),
  locationId: z.string().nullable(),
  locationName: z.string().nullable(),
  quantity: z.number(),
  reservedQuantity: z.number(),
  certNumber: z.string().nullable(),
  acquiredAt: isoDate,
});
export type CardLot = z.output<typeof CardLot>;

export const CardScan = z.object({
  id: z.string(),
  kind: z.enum(["OWNER", "CUSTOMER"]),
  review: z.enum(["PENDING", "CONFIRMED", "REJECTED"]),
  timestamp: isoDate,
  imageUrl: z.string(),
  customerName: z.string().nullable(),
});
export type CardScan = z.output<typeof CardScan>;

/** One card in full: its listing, lots, the scans behind it, and what has moved. */
export const CardDetail = CardListItem.extend({
  listingPriceCents: z.number().nullable(),
  lots: z.array(CardLot),
  scans: z.array(CardScan),
  soldCount: z.number(),
  openQuotes: z.number(),
});
export type CardDetail = z.output<typeof CardDetail>;

/** A new total on hand, or `trackStock: false` to stop counting (only at zero). */
export interface CardStockUpdate {
  quantity?: number;
  /** ADJUSTED is a miscount found; SHRINKAGE is stock that is gone. Default ADJUSTED. */
  kind?: "ADJUSTED" | "SHRINKAGE";
  reason?: string;
  locationId?: string;
  trackStock?: boolean;
}

export const StockLevel = z.object({
  delta: z.number(),
  onHand: z.number(),
});
export type StockLevel = z.output<typeof StockLevel>;

export const StoreCustomer = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  isGuest: z.boolean(),
  /** Created by the shop at its counter (POS); never signs in. */
  isPos: z.boolean(),
  scanCount: z.number(),
  lastScanAt: isoDate,
});
export type StoreCustomer = z.output<typeof StoreCustomer>;

/** What a card edit may change. Absent keeps the value. */
export type CardEdit = Partial<{
  player: string;
  team: string;
  brand: string;
  year: number;
  cardNumber: string;
  parallelType: string;
  insertName: string;
  cardType: string;
  /** PSA, BGS, … or "" for raw. */
  gradingCompany: string;
  /** 0 for raw. */
  grade: number;
  slabbed: boolean;
  setName: string | null;
  cardFullName: string | null;
}>;

export const CardEditResult = z.object({
  card: CardSummary,
  /** The card is now a different card: its pricing product and comps were cleared, so reprice it. */
  identityChanged: z.boolean(),
});
export type CardEditResult = z.output<typeof CardEditResult>;
